// src/hooks/useCookieConsent.js
import { useCallback, useEffect, useState } from "react";

const LS_KEY = "cookie_consent_v1";
const DEFAULT = { responded:false, functional:false, analytics:false, marketing:false, timestamp:null };

export function useCookieConsent() {
  const [consent, setConsent] = useState(DEFAULT);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setConsent({ ...DEFAULT, ...JSON.parse(raw) });
    } catch {}
  }, []);

  // 🔔 Listen for global “open-cookie-settings” (and optional close) events
  useEffect(() => {
    const open = () => setSettingsOpen(true);
    const close = () => setSettingsOpen(false);
    window.addEventListener("open-cookie-settings", open);
    window.addEventListener("close-cookie-settings", close);
    return () => {
      window.removeEventListener("open-cookie-settings", open);
      window.removeEventListener("close-cookie-settings", close);
    };
  }, []);

  const persist = useCallback((next) => {
    const withTs = { ...next, timestamp: Date.now() };
    setConsent(withTs);
    try { localStorage.setItem(LS_KEY, JSON.stringify(withTs)); } catch {}
  }, []);

  const acceptAll = useCallback(() => {
    persist({ ...consent, responded:true, functional:true, analytics:true, marketing:true });
    setSettingsOpen(false);
  }, [consent, persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ ...consent, responded:true, functional:false, analytics:false, marketing:false });
    setSettingsOpen(false);
  }, [consent, persist]);

  const savePreferences = useCallback((prefs) => {
    persist({ ...consent, responded:true, ...prefs });
    setSettingsOpen(false);
  }, [consent, persist]);

  return { consent, acceptAll, rejectNonEssential, savePreferences, settingsOpen, setSettingsOpen };
}
